import { getFullUrlStaticFilePDF, getServerBaseUrl } from "../helps";
import {
  actionUploadRecruitmentCV,
  actionUploadRecruitmentFile,
} from "../../pages/desktop/chatbot-recruiment/action";

export const CV_ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];
export const CV_ACCEPT_ATTR =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const CERTIFICATE_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".bmp",
];
export const CERTIFICATE_ACCEPT_ATTR =
  ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.bmp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/gif,image/webp,image/bmp";
export const CV_MAX_BYTES = 50 * 1024 * 1024;

export const getFileExtension = (filename = "") => {
  const dot = filename.lastIndexOf(".");
  return dot >= 0 ? filename.slice(dot).toLowerCase() : "";
};

/** Extension không có dấu chấm: pdf, docx, ... */
export const getFileExtensionKey = (filename = "") => {
  const ext = getFileExtension(filename);
  return ext ? ext.replace(/^\./, "") : "";
};

export const extractCvRecordFromQtResponse = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.cv && typeof payload.cv === "object") return payload.cv;
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) return payload.data;
  if (payload.result && typeof payload.result === "object") return payload.result;
  if (payload.id != null || payload.cv_id != null || payload.cv_path || payload.path) {
    return payload;
  }
  return null;
};

export const extractCvRecordsFromQtResponse = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.items)) return payload.items.filter(Boolean);

  const single = extractCvRecordFromQtResponse(payload);
  return single ? [single] : [];
};


export const isQtCvUploadSuccess = (response) => {
  const httpOk = response?.status >= 200 && response?.status < 300;
  const payload = response?.data;

  if (!payload) return httpOk;

  if (payload.success === false || payload.status === false) {
    return false;
  }

  if (payload.success === true || payload.status === true) {
    return true;
  }

  if (typeof payload.code === "number" && payload.code >= 400) {
    return false;
  }

  if (extractCvRecordFromQtResponse(payload)) {
    return true;
  }

  return httpOk;
};

export const getQtCvUploadError = (response) => {
  const payload = response?.data;
  return (
    payload?.error ||
    payload?.message ||
    payload?.msg ||
    (typeof payload?.code === "number" && payload.code >= 400
      ? payload.message || payload.msg
      : null)
  );
};

export const mapCvToAttachment = (cvRecord, fileUrls = []) => {
  if (!cvRecord && (!fileUrls || fileUrls.length === 0)) return null;

  const uploadsFromUrls = (fileUrls || []).find((url) =>
    String(url).includes("uploads/"),
  );
  const uploadsFromRecord =
    cvRecord?.cv_path && String(cvRecord.cv_path).includes("uploads/")
      ? cvRecord.cv_path
      : null;
  const path =
    uploadsFromUrls ||
    uploadsFromRecord ||
    cvRecord?.cv_path ||
    cvRecord?.path ||
    fileUrls?.[0] ||
    cvRecord?.url ||
    "";
  const name =
    cvRecord?.original_name ||
    cvRecord?.original_filename ||
    cvRecord?.cv_file ||
    cvRecord?.name ||
    "cv";

  if (!path && !name) return null;

  const extension = getFileExtensionKey(name || path);

  return {
    name,
    original_name: name,
    cv_path: path,
    path,
    file_path: path,
    file_url: path,
    doc_path: path,
    extension,
    extension_file: extension,
    candidate_id: cvRecord?.candidate_id,
  };
};

/** Lấy đường dẫn file từ attachment (ưu tiên cv_path uploads/) */
export const getAttachmentFilePath = (attachment) => {
  if (!attachment) return "";
  return (
    attachment.cv_path ||
    attachment.path ||
    attachment.url ||
    attachment.file_path ||
    attachment.file_url ||
    ""
  );
};

export const getAttachmentFileName = (attachment, fallback = "file") => {
  if (!attachment) return fallback;
  return (
    attachment.original_name ||
    attachment.name ||
    attachment.file_name ||
    attachment.filename ||
    fallback
  );
};

/** Cắt extension từ metadata, tên file hoặc cv_path — vd. uploads/.../file.docx → docx */
export const getAttachmentExtension = (attachment, filename = "") => {
  const fromMeta = attachment?.extension || attachment?.extension_file || "";
  if (fromMeta) {
    return String(fromMeta).replace(/^\./, "").toLowerCase();
  }
  const name = filename || getAttachmentFileName(attachment, "");
  const path = getAttachmentFilePath(attachment);
  return getFileExtensionKey(name || path);
};

/** Chuẩn hóa attachment chat: path, tên, extension, download_url */
export const normalizeChatAttachment = (attachment, fallbackName = "file") => {
  if (!attachment || typeof attachment !== "object") return null;

  const cvPath = getAttachmentFilePath(attachment);
  const name = getAttachmentFileName(attachment, fallbackName);
  const extension = getAttachmentExtension(attachment, name);
  const withPath = {
    ...attachment,
    cv_path: cvPath || attachment.cv_path,
    extension,
  };
  const downloadUrl = buildRecruitmentFileDownloadUrl(withPath);

  return {
    ...withPath,
    download_url: downloadUrl,
  };
};

export const buildAttachmentViewRecord = (attachment, fallbackName = "file") =>
  normalizeChatAttachment(attachment, fallbackName);

export const buildRecruitmentFileDownloadUrl = (attachment) => {
  if (!attachment || typeof attachment !== "object") return null;

  const cvPath = attachment.cv_path || attachment.path;
  const name = attachment.original_name || attachment.name || "CV";

  if (!cvPath || !String(cvPath).includes("uploads/")) {
    return null;
  }

  const params = new URLSearchParams({ cv_path: cvPath, name });
  return `/api/recruitment/files/download?${params.toString()}`;
};

const isLocalDevHost = () => {
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
};

/** URL tải file đầy đủ: local dùng REACT_APP_SERVER_BASE_URL, production dùng domain hiện tại */
export const resolveRecruitmentDownloadFullUrl = (attachment) => {
  const relativeUrl = buildRecruitmentFileDownloadUrl(attachment);
  if (!relativeUrl) return null;

  const configuredBase = getServerBaseUrl();
  if (configuredBase && isLocalDevHost()) {
    return `${String(configuredBase).replace(/\/$/, "")}${relativeUrl}`;
  }

  return `${window.location.origin}${relativeUrl}`;
};

/** URL xem trước ảnh chứng chỉ (uploads/...) — inline, không qua download attachment */
export const resolveRecruitmentImagePreviewUrl = (attachment) => {
  const path = getAttachmentFilePath(attachment);
  if (!path || !String(path).includes("uploads/")) {
    return null;
  }
  return getFullUrlStaticFilePDF(path);
};

const IMAGE_EXTENSION_KEYS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
]);

export const isImageAttachment = (attachment) =>
  IMAGE_EXTENSION_KEYS.has(getAttachmentExtension(attachment));

/** Chỉ chứng chỉ dạng ảnh mới preview bằng antd Image */
export const isCertificateImageAttachment = (attachment) =>
  attachment?.file_kind === "certificate" && isImageAttachment(attachment);

/** Chuẩn hóa attachment để nút tải gọi GET /api/recruitment/files/download?cv_path=... */
export const resolveChatMessageAttachment = (message) => {
  const raw = message?.attachment;

  if (raw) {
    return normalizeChatAttachment(raw);
  }

  const text = String(message?.text ?? "").trim();
  const legacyName = text.replace(/^.*đã gửi file:\s*/i, "").trim();
  const isFileMessage =
    message?.type === "file" ||
    /đã gửi file/i.test(text) ||
    Boolean(legacyName);

  if (!isFileMessage) return null;

  return normalizeChatAttachment(
    { name: legacyName || "CV" },
    legacyName || "CV",
  );
};

export const isAllowedCvFile = (file) => {
  if (!file?.name) return false;
  return CV_ALLOWED_EXTENSIONS.includes(getFileExtension(file.name));
};

export const isAllowedCertificateFile = (file) => {
  if (!file?.name) return false;
  return CERTIFICATE_ALLOWED_EXTENSIONS.includes(getFileExtension(file.name));
};

/** pdf: xem trước trong trình duyệt; office: doc/docx */
export const getCvPreviewMode = (file) => {
  if (!file?.name) return null;
  const ext = getFileExtension(file.name);
  if (ext === ".pdf") return "pdf";
  if (ext === ".doc" || ext === ".docx") return "office";
  return null;
};

/** image: xem trước ảnh; pdf/office: giống CV */
export const getCertificatePreviewMode = (file) => {
  if (!file?.name) return null;
  const ext = getFileExtension(file.name);
  if ([".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"].includes(ext)) {
    return "image";
  }
  return getCvPreviewMode(file);
};

export const mapCertificateToAttachment = (cert, candidateId) => {
  const path = cert?.path || "";
  if (!path) return null;

  const name = cert?.name || "chứng chỉ";
  const extension = getFileExtensionKey(name || path);

  return {
    name,
    original_name: name,
    cv_path: path,
    path,
    file_path: path,
    file_url: path,
    extension,
    extension_file: extension,
    candidate_id: candidateId,
    file_kind: "certificate",
  };
};

export const enrichCertificatesInAttachment = (attachment) => {
  if (!attachment || typeof attachment !== "object") return attachment;

  const certificates = (attachment.certificates || [])
    .map((cert) => {
      const mapped = mapCertificateToAttachment(cert, attachment.candidate_id);
      if (!mapped) return null;
      return {
        ...mapped,
        download_url: buildRecruitmentFileDownloadUrl(mapped),
      };
    })
    .filter(Boolean);

  const normalized = normalizeChatAttachment(attachment, attachment.original_name || attachment.name || "CV");

  if (!certificates.length) return normalized;

  return {
    ...normalized,
    certificates,
  };
};

/** Chuẩn hóa messages từ API session/chat — giữ certificates + download_url */
export const normalizeApiChatMessages = (messages = [], sessionCertificates = []) => {
  if (!Array.isArray(messages)) return [];

  return messages.map((message) => {
    if (message?.type !== "file" || !message?.attachment) {
      return message;
    }

    const certificates =
      message.attachment.certificates?.length > 0
        ? message.attachment.certificates
        : sessionCertificates;

    return {
      ...message,
      attachment: enrichCertificatesInAttachment({
        ...message.attachment,
        certificates,
      }),
    };
  });
};

export const mapUploadedFileToPayload = (record, fileUrls = []) => {
  const attachment = mapCvToAttachment(record, fileUrls);
  if (!attachment) return null;

  return {
    name: attachment.name,
    url: attachment.cv_path || attachment.path || "",
    cv_path: attachment.cv_path,
    path: attachment.path,
    type: record?.content_type || record?.type || "",
    id: record?.id,
    candidate_id: record?.candidate_id,
    extension: attachment.extension,
  };
};

export const buildChatMessagesFromUploadPayload = (payload) => {
  if (!payload) return [];

  const cvRecord = payload.cv || payload.data;
  const includeCvInUi = payload.uploaded_cv_in_request !== false;
  const attachment = includeCvInUi
    ? mapCvToAttachment(cvRecord, payload.file_urls)
    : null;
  const payloadCertificates = Array.isArray(payload.certificates)
    ? payload.certificates
    : [];
  const stableSuffix =
    cvRecord?.id != null ? `cv-${cvRecord.id}` : `${Date.now()}`;

  const resolveCertificates = (messageAttachment, itemAttachment) =>
    messageAttachment?.certificates ||
    itemAttachment?.certificates ||
    payloadCertificates ||
    [];

  const buildUserFileMessage = () => {
    const apiUserAttachment = payload.messages?.find(
      (item) => item.from === "user",
    )?.attachment;
    const certificates = resolveCertificates(apiUserAttachment, null);

    if (!attachment && certificates.length === 0) return null;

    return {
      id: `user-cv-${stableSuffix}`,
      from: "user",
      text: "",
      type: "file",
      attachment: enrichCertificatesInAttachment({
        ...(attachment || {}),
        ...(apiUserAttachment || {}),
        candidate_id:
          cvRecord?.candidate_id ??
          attachment?.candidate_id ??
          apiUserAttachment?.candidate_id,
        cv_path:
          apiUserAttachment?.cv_path ||
          (includeCvInUi
            ? attachment?.cv_path || payload.cv_path
            : undefined),
        certificates,
      }),
    };
  };

  const apiMessages = payload.messages;
  if (Array.isArray(apiMessages) && apiMessages.length > 0) {
    return normalizeApiChatMessages(
      apiMessages.map((item, index) => {
        const from = item.from === "user" ? "user" : "bot";
        const message = {
          id:
            item.id != null
              ? item.id
              : from === "user"
                ? `user-cv-${stableSuffix}`
                : `bot-cv-${stableSuffix}-${index}`,
          from,
          text: item.text ?? item.content ?? "",
          type: item.type,
          attachment: item.attachment,
        };

        if (from === "user") {
          const certificates = resolveCertificates(
            message.attachment,
            item.attachment,
          );

          if (message.attachment || attachment || certificates.length > 0) {
            message.attachment = enrichCertificatesInAttachment({
              ...(attachment || {}),
              ...(message.attachment || item.attachment || {}),
              cv_path:
                message.attachment?.cv_path ||
                item.attachment?.cv_path ||
                (includeCvInUi
                  ? attachment?.cv_path || payload.cv_path
                  : undefined),
              candidate_id:
                message.attachment?.candidate_id ??
                item.attachment?.candidate_id ??
                cvRecord?.candidate_id ??
                attachment?.candidate_id,
              certificates,
            });
            message.text = "";
            message.type = "file";
          }
        }

        return message;
      }),
      payloadCertificates,
    );
  }

  const result = [];
  const userFileMessage = buildUserFileMessage();
  if (userFileMessage) {
    result.push(userFileMessage);
  }

  const aiText = (payload.ai_response || "").trim();
  if (aiText) {
    result.push({
      id: `bot-cv-${stableSuffix}`,
      from: "bot",
      text: aiText,
    });
  }

  return result;
};

const appendUploadContextToFormData = (formData, uploadContext) => {
  if (!uploadContext) return;

  const {
    userId,
    jobContext,
    session,
    userInfo,
    candidate,
  } = uploadContext;

  if (userId) {
    formData.append("user_id", String(userId));
  }
  if (jobContext) {
    formData.append("job_context", jobContext);
  }
  if (session) {
    formData.append("session", String(session));
    formData.append("session_id", String(session));
  }
  if (userInfo) {
    formData.append("user_info", JSON.stringify(userInfo));
  }
  if (candidate) {
    formData.append("candidate", JSON.stringify(candidate));
  }
};

const buildRepositoryUploadFormData = ({
  cvFile,
  certificateFiles = [],
  campaignId,
  candidateId,
  uploadContext,
}) => {
  const formData = new FormData();
  if (cvFile) {
    formData.append("file", cvFile);
  }
  (certificateFiles || []).filter(Boolean).forEach((certFile) => {
    formData.append("certificate_files", certFile);
  });
  if (campaignId != null && campaignId !== "") {
    formData.append("campaign_id", String(campaignId));
  }
  if (candidateId) {
    formData.append("candidate_id", String(candidateId));
  }
  appendUploadContextToFormData(formData, uploadContext);
  return formData;
};

export const executeRecruitmentCvUpload = async ({
  file,
  certificateFiles = [],
  campaignId,
  candidateId,
  uploadContext,
}) => {
  if (!campaignId) {
    throw new Error("Thiếu thông tin chiến dịch. Vui lòng tải lại trang.");
  }

  if (!uploadContext?.userId || !uploadContext?.session) {
    throw new Error("Thiếu thông tin phiên chat. Vui lòng tải lại trang.");
  }

  const candidatePayload = uploadContext?.candidate;
  if (!candidateId && !candidatePayload?.email) {
    throw new Error(
      "Thiếu thông tin ứng viên. Vui lòng điền họ tên, email và số điện thoại.",
    );
  }

  const buildFormData = () => {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
      formData.append("is_cv", "true");
    }
    if (campaignId != null && campaignId !== "") {
      formData.append("campaign_id", String(campaignId));
    }
    if (candidateId) {
      formData.append("candidate_id", String(candidateId));
    }
    (certificateFiles || []).filter(Boolean).forEach((certFile) => {
      formData.append("certificate_files", certFile);
    });
    appendUploadContextToFormData(formData, uploadContext);
    return formData;
  };

  const fileResponse = await actionUploadRecruitmentFile(buildFormData(), campaignId);
  const filePayload = fileResponse?.data;

  if (!filePayload?.success) {
    throw new Error(filePayload?.error || "Tải file thất bại");
  }

  const resolvedCandidateId =
    candidateId ?? filePayload?.candidate?.id ?? filePayload?.candidate_id;

  if (!resolvedCandidateId) {
    throw new Error("Thiếu candidate_id. Vui lòng tải lại trang.");
  }

  const repositoryFormData = buildRepositoryUploadFormData({
    cvFile: file,
    certificateFiles,
    campaignId,
    candidateId: resolvedCandidateId,
    uploadContext,
  });
  const cvResponse = await actionUploadRecruitmentCV(
    resolvedCandidateId,
    repositoryFormData,
    campaignId,
  );
  const cvPayload = cvResponse?.data;

  if (!isQtCvUploadSuccess(cvResponse)) {
    throw new Error(getQtCvUploadError(cvResponse) || "Lưu CV thất bại");
  }

  if (file && !filePayload?.ai_response && !(filePayload?.messages?.length > 0)) {
    throw new Error("Tải file thất bại");
  }

  const cvRecords = extractCvRecordsFromQtResponse(cvPayload);
  const cvRecord =
    cvRecords.find((item) => item?.is_primary === true)
    || cvRecords[0]
    || extractCvRecordFromQtResponse(cvPayload);
  const payload = {
    ...filePayload,
    cv: cvRecord,
    data: cvRecord,
    certificates: filePayload.certificates || [],
    file_urls: filePayload.file_urls || cvPayload?.file_urls,
    uploaded_cv_in_request: Boolean(file),
  };

  const filePayloadForUi = file
    ? mapUploadedFileToPayload(
      cvRecord,
      payload.file_urls,
    )
    : null;

  const uploadedCertificateFiles = (filePayload.certificates || [])
    .map((cert) => mapCertificateToAttachment(cert, resolvedCandidateId))
    .filter(Boolean)
    .map((att) => ({
      name: att.name,
      url: att.path,
      cv_path: att.path,
      path: att.path,
      extension: att.extension,
      candidate_id: resolvedCandidateId,
      file_kind: "certificate",
    }));

  const allFiles = [
    ...(filePayloadForUi ? [filePayloadForUi] : []),
    ...uploadedCertificateFiles,
  ];
  const allMessages = Array.isArray(payload.messages) ? [...payload.messages] : [];

  return {
    files: allFiles,
    payload: {
      ...payload,
      messages: allMessages,
      repository_items: cvRecords,
    },
  };
};
